import type { AnonymousIdProvider, AnonymousOptions } from '../types';
import { logger } from '../internal/logger';
import {
  STORAGE_KEYS,
  storageGetItem,
  storageSetItem,
} from '../internal/storageAdapter';

export interface ResolvedAnonymousId {
  anonymousId: string;
  provider: AnonymousIdProvider;
}

const DEFAULT_FP_CDN_URL = 'https://openfpcdn.io/fingerprintjs/v4';

type FingerprintAgent = { load: () => Promise<{ get: () => Promise<{ visitorId: string }> }> };

export function generateInternalAnonymousId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function tryFingerprintCdn(url: string): Promise<string | null> {
  try {
    const mod = await import(/* @vite-ignore */ /* webpackIgnore: true */ url);
    const fp = ((mod as { default?: FingerprintAgent }).default ?? mod) as FingerprintAgent;
    const agent = await fp.load();
    const result = await agent.get();
    return result.visitorId || null;
  } catch {
    return null;
  }
}

function persist(resolved: ResolvedAnonymousId): void {
  storageSetItem(STORAGE_KEYS.anonymousId, resolved.anonymousId);
  storageSetItem(STORAGE_KEYS.anonymousIdProvider, resolved.provider);
}

export async function resolveAnonymousId(
  options: AnonymousOptions | undefined
): Promise<ResolvedAnonymousId> {
  const persistedId = storageGetItem(STORAGE_KEYS.anonymousId);
  if (persistedId) {
    const persistedProvider = storageGetItem(STORAGE_KEYS.anonymousIdProvider);
    const provider: AnonymousIdProvider =
      persistedProvider === 'fingerprintjs' ? 'fingerprintjs' : 'internal';
    return { anonymousId: persistedId, provider };
  }

  if (options?.getVisitorId) {
    try {
      const id = await options.getVisitorId();
      if (id) {
        const resolved: ResolvedAnonymousId = { anonymousId: id, provider: 'fingerprintjs' };
        persist(resolved);
        return resolved;
      }
    } catch (e) {
      logger.warn('anonymous getVisitorId callback failed', e);
    }
  }

  if (options?.optOut === false) {
    const fpId = await tryFingerprintCdn(options?.fingerprintCdnUrl ?? DEFAULT_FP_CDN_URL);
    if (fpId) {
      const resolved: ResolvedAnonymousId = { anonymousId: fpId, provider: 'fingerprintjs' };
      persist(resolved);
      return resolved;
    }
  }

  const resolved: ResolvedAnonymousId = { anonymousId: generateInternalAnonymousId(), provider: 'internal' };
  persist(resolved);
  return resolved;
}
